import React, { useEffect, useState, useRef } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const Inductions = () => {
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters & Responsive
    const [search, setSearch] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Modal State
    const [editingPerson, setEditingPerson] = useState(null);
    const [newDate, setNewDate] = useState('');
    
    // Signature
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSigned, setHasSigned] = useState(false);
    const [viewingSignature, setViewingSignature] = useState(null);

    useEffect(() => { 
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        fetchInductions(); 
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchInductions = async () => {
        try {
            const res = await api.get('inductions/');
            setPeople(res.data);
            setLoading(false);
        } catch (err) { console.error(err); setLoading(false); }
    };

    // Signature Logic
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.beginPath();
        ctx.moveTo(clientX - rect.left, clientY - rect.top);
        setIsDrawing(true);
        setHasSigned(true);
    };
    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        ctx.lineTo(clientX - rect.left, clientY - rect.top);
        ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
    };
    const stopDrawing = () => { setIsDrawing(false); };
    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSigned(false);
    };

    const handleUpdate = async () => {
        if (!editingPerson) return;
        let signatureData = null;
        if (newDate && hasSigned && canvasRef.current) signatureData = canvasRef.current.toDataURL('image/png');
        try {
            await api.post('inductions/', {
                db_type: editingPerson.db_type, id: editingPerson.id,
                date: newDate || null, signature: signatureData 
            });
            alert("✅ Induction Updated");
            setEditingPerson(null); setHasSigned(false); fetchInductions(); 
        } catch (err) { alert("Update failed"); }
    };

    const filteredPeople = people.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.company.toLowerCase().includes(search.toLowerCase());
        const matchesCompany = filterCompany ? p.company === filterCompany : true;
        return matchesSearch && matchesCompany;
    });

    const companies = [...new Set(people.map(p => p.company))];
    const inputStyle = { padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', minWidth: isMobile ? '100%' : '200px' };

    if (loading) return <div style={{color:'white'}}>Loading Registry...</div>;

    return (
        <div className="animate-fade-in">
            {/* HEADER & FILTERS */}
            <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0', alignItems: isMobile ? 'flex-start' : 'center'}}>
                    <h2 style={{color: 'white', margin:0}}>🦺 Induction Registry</h2>
                    <div style={{display:'flex', gap:'15px', color: THEME.textSec, fontSize:'0.9rem', flexWrap:'wrap'}}>
                        <span>Total: <strong style={{color:'white'}}>{people.length}</strong></span>
                        <span>Inducted: <strong style={{color: THEME.success}}>{people.filter(p => p.is_inducted).length}</strong></span>
                        <span>Pending: <strong style={{color: THEME.danger}}>{people.filter(p => !p.is_inducted).length}</strong></span>
                    </div>
                </div>
                
                <div style={{display:'flex', gap:'15px', flexWrap:'wrap', flexDirection: isMobile ? 'column' : 'row'}}>
                    <input type="text" placeholder="🔍 Search Name..." value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
                    <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={inputStyle}>
                        <option value="">All Companies</option>
                        {companies.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            </div>

            {/* TABLE - SCROLL WRAPPER */}
            <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', minWidth: '800px', textAlign: 'left', color: THEME.textSec, borderCollapse: 'collapse', fontSize:'0.9rem'}}>
                        <thead style={{background: '#020617', borderBottom: `1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px'}}>Name</th>
                                <th>Company</th>
                                <th>Role</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Inducted By</th>
                                <th>Signature</th>
                                <th style={{textAlign:'right', paddingRight:'15px'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPeople.map((p, idx) => (
                                <tr key={`${p.db_type}-${p.id}`} style={{borderBottom: `1px solid ${THEME.border}`}}>
                                    <td style={{padding: '12px 15px', fontWeight:'bold', color:'white'}}>{p.name}</td>
                                    <td style={{color: THEME.warning}}>{p.company}</td>
                                    <td>{p.role}</td>
                                    <td><span style={{fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', background: p.db_type === 'user' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)', color: p.db_type === 'user' ? THEME.primary : '#a855f7', border: `1px solid ${p.db_type === 'user' ? THEME.primary : '#a855f7'}`}}>{p.db_type === 'user' ? 'Account' : 'Worker'}</span></td>
                                    <td>
                                        {p.is_inducted ? 
                                            <span style={{color: THEME.success, fontWeight:'bold', background:'rgba(16, 185, 129, 0.1)', padding:'2px 8px', borderRadius:'4px', whiteSpace:'nowrap'}}>✅ {p.induction_date}</span> : 
                                            <span style={{color: THEME.danger, fontWeight:'bold', background:'rgba(239, 68, 68, 0.1)', padding:'2px 8px', borderRadius:'4px', whiteSpace:'nowrap'}}>❌ Pending</span>
                                        }
                                    </td>
                                    <td style={{color: p.inducted_by_name !== '-' ? THEME.primary : THEME.textSec}}>{p.inducted_by_name}</td>
                                    <td>
                                        {p.induction_signature ? (
                                            <div onClick={() => setViewingSignature(p.induction_signature)} style={{width: '60px', height: '30px', background: 'white', borderRadius: '4px', cursor: 'pointer', overflow: 'hidden', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                <img src={p.induction_signature} alt="Sig" style={{maxHeight: '100%', maxWidth: '100%'}} />
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td style={{textAlign:'right', paddingRight:'15px'}}>
                                        <button onClick={() => { setEditingPerson(p); setNewDate(p.induction_date || new Date().toISOString().split('T')[0]); }} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color: THEME.primary, padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Update</button>
                                    </td>
                                </tr>
                            ))}
                            {filteredPeople.length === 0 && <tr><td colSpan="8" style={{padding:'30px', textAlign:'center', color: THEME.textSec}}>No results found.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SIGNATURE PREVIEW */}
            {viewingSignature && (
                <div onClick={() => setViewingSignature(null)} style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}>
                    <div style={{background: 'white', padding: '15px', borderRadius: '12px', maxWidth: '90%'}} onClick={e => e.stopPropagation()}>
                        <img src={viewingSignature} style={{maxWidth: '100%', maxHeight: '300px', border: '1px solid #ccc', borderRadius:'4px'}} />
                        <button onClick={() => setViewingSignature(null)} style={{display:'block', width:'100%', padding:'12px', marginTop:'15px', background: THEME.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Close Preview</button>
                    </div>
                </div>
            )}

            {/* UPDATE MODAL - MOBILE RESPONSIVE */}
            {editingPerson && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
                    <div style={{background: THEME.card, padding:'30px', borderRadius:'12px', border:`1px solid ${THEME.primary}`, width: isMobile ? '90%' : '400px', boxShadow:'0 0 40px rgba(0,0,0,0.5)'}}>
                        <h3 style={{marginTop:0, color:'white'}}>✍️ Digital Induction</h3>
                        <p style={{color: THEME.textSec, marginBottom:'20px'}}>Inducting <strong>{editingPerson.name}</strong> ({editingPerson.company})</p>
                        
                        <label style={{display:'block', color: THEME.textSec, marginBottom:'5px', fontSize:'0.9rem'}}>Date Completed</label>
                        <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'20px', boxSizing:'border-box'}} />

                        {newDate && (
                            <div style={{marginBottom:'20px'}}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px'}}>
                                    <label style={{color: THEME.textSec, fontSize:'0.9rem'}}>Sign Below:</label>
                                    <button onClick={clearSignature} style={{fontSize:'0.8rem', background:'transparent', border:'none', color: THEME.danger, cursor:'pointer'}}>Clear</button>
                                </div>
                                {/* Touch Action None is CRITICAL for mobile drawing */}
                                <div style={{background:'white', borderRadius:'6px', overflow:'hidden', border: `2px dashed ${THEME.border}`, touchAction: 'none'}}>
                                    <canvas ref={canvasRef} width={isMobile ? 300 : 340} height={150} style={{display:'block', cursor:'crosshair'}}
                                        onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
                                </div>
                                {!hasSigned && <div style={{color: THEME.danger, fontSize:'0.8rem', marginTop:'5px'}}>* Signature required to save.</div>}
                            </div>
                        )}

                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={() => { setEditingPerson(null); setHasSigned(false); }} style={{flex:1, padding:'12px', background:'transparent', border:`1px solid ${THEME.border}`, color: THEME.textSec, borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                            <button onClick={handleUpdate} disabled={newDate && !hasSigned} style={{flex:1, padding:'12px', background: (newDate && !hasSigned) ? '#334155' : THEME.success, color:'white', border:'none', borderRadius:'6px', cursor: (newDate && !hasSigned) ? 'not-allowed' : 'pointer', fontWeight:'bold'}}>Confirm & Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inductions;